import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from "@ngx-translate/core";

import { ParamData, PeriodData } from "@otimo/highcharts-parameters";
import { ChartConfigBase, ClickDataPoint, DrillDownInfo, ElearningInfo, HighChartDataBase, XmlReportDataService } from "@otimo/highcharts-xmlreport";
import { MessagingService } from "@otimo/messaging";
import moment from "moment";
import { BehaviorSubject, Observable, Subscription } from "rxjs";

import { DrillDownConfig } from './drill-down-config';
import { ParamSetting } from './param-setting';
import { ReportConfig } from './report-config';

import { LangService } from '../services/lang.service';
import { RouteDataService } from '../services/route-data.service';
import { SharedReportsService } from '../services/shared-reports.service';

export abstract class ReportPageBase
{
    // Common strings
    protected savedFilterQueryName = "filter";
    protected saveFilterForAllReportsName = "saveFilterForAllPortalReports";
    protected commonFiltersForAllReportsName = "commonFiltersForAllPortalReports";

    // Report settings
    protected chartWidth = 900;
    protected chartHeight = 400;
    protected reportId: string;
    protected reportConfig: ReportConfig;
    public config: BehaviorSubject<ChartConfigBase> = new BehaviorSubject<ChartConfigBase>(null);
    protected prevConfig: ChartConfigBase;
    public configHasDefaultValues = new BehaviorSubject<boolean>(true);
    public showHighcharts = new BehaviorSubject<boolean>(true);
    public language: BehaviorSubject<string> = new BehaviorSubject<string>("");
    protected configSub: Subscription;
    protected groupingsSub: Subscription;

    // Translate
    protected langSub: Subscription;
    protected transSub: Subscription;
    public langHasInit = new BehaviorSubject<boolean>(false);

    // SaveUserReport
    protected currentSharedReportId: string;
    public canSaveUserReport: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    // Params
    public params: BehaviorSubject<ParamData[]> = new BehaviorSubject<ParamData[]>([]);
    public paramSettings = new BehaviorSubject<ParamSetting>(new ParamSetting());
    public saveFiltersForAllReports = new BehaviorSubject<boolean>(false);

    // Groupings
    public groupings: BehaviorSubject<string[]> = new BehaviorSubject<string[]>(null);
    public currentGrouping = new BehaviorSubject<string>(null);

    // Drilldown
    public drillDownInfo: DrillDownInfo = new DrillDownInfo();
    protected drillDownConfig: DrillDownConfig = new DrillDownConfig();

    // Elearning
    public elearningInfo: ElearningInfo = null;


    constructor(
        protected location: Location,
        protected route: ActivatedRoute,
        protected router: Router,
        protected xmlReportService: XmlReportDataService,
        protected sharedReportsService: SharedReportsService,
        protected messaging: MessagingService,
        protected routeDataService: RouteDataService,
        protected langService: LangService,
        protected translate: TranslateService
    ) { 
        this.groupings = this.xmlReportService.currentGroupings;
    }

    /* Anpassad navigering för att lägga på språk i url */
    protected navigateTo(routeUrl: string) {
        let lang = LangService.currentLang.getValue();
        this.router.navigate([lang + "/" + routeUrl]);
    }
    protected locationGo(url: string, query?: string) {
        let lang = LangService.currentLang.getValue();
        this.location.go(lang + "/" + url, query);
    }

    /* Laddar rapport-config */
    protected abstract loadReportConfig()

    /* Laddar urvalsinställningar för rapporten */
    protected loadParamSettings(): void {
        if (this.reportConfig) 
            this.paramSettings.next(this.reportConfig.paramSettings);
    }

    /* Laddar eventuell drilldown för rapporten */
    protected loadDrillDown(): void {
        this.drillDownConfig = this.routeDataService.getSingleReadData();
        if (this.reportConfig) {
            this.drillDownInfo = new DrillDownInfo(
                this.reportConfig.canDrillDown || this.reportConfig.canSerieDrillDown,
                this.drillDownConfig?.isDrillDown,
                this.drillDownConfig?.parentId,
                this.drillDownConfig?.parentName
            );
        }
    }

    /* Laddar eventuell E-learning för rapporten */
    protected loadElearning(): void {
        if (this.reportConfig) 
            this.elearningInfo = this.reportConfig.elearning;
    }

    /* Innehåller logik för att bestämma vilka filter som ska användas i rapporten */
    protected abstract loadConfig(): void

    /* Lagring till LS för "Spara urval för alla rapporter" */
    public changeSaveForAllReports(isSaving: boolean) {
        localStorage.setItem(this.saveFilterForAllReportsName, isSaving.toString());
        this.updateCommonFilters();
    }

    /* Sätter eller raderar gemensamma urval för alla rapporter */
    protected updateCommonFilters() {
        if (this.isSavingForAllReports())
            this.saveCommonFilters();
        else
            this.deleteCommonFilters();
    }

    /* Tar aktuella urval och sätter som gemensamma */
    protected saveCommonFilters() {
        localStorage.setItem(this.commonFiltersForAllReportsName, JSON.stringify(this.config.getValue()));
    }

    /* Hämtar gemensamma urval och använder för aktuell rapport */
    protected abstract getCommonFilters()

    /* Raderar gemensamma urval */
    protected deleteCommonFilters() {
        localStorage.removeItem(this.commonFiltersForAllReportsName);
    }

    /* Om det finns språk-inställningar, spara ner det i config */
    protected abstract setLanguageInConfig()

    /* Är "Spara urval för alla rapporter" ikryssat? */
    protected isSavingForAllReports(): boolean {
        return localStorage.getItem(this.saveFilterForAllReportsName) === "true";
    }

    /* Tar emot chartData från xml-report-component */
    public chartData = new BehaviorSubject<HighChartDataBase>(null);
    async receiveChartData(chartData: HighChartDataBase) {
        this.chartData.next(chartData);
        this.currentGrouping.next(chartData.grouping);

        if (chartData.skipParams && chartData.skipParams.length > 0) {
            let settings = this.paramSettings.getValue();
            settings.skipParams = settings.skipParams.concat(chartData.skipParams);
            this.paramSettings.next(settings);
        }
    }

    /* Drilldown: T.ex. en kolumn har klickats */
    public abstract clickedCategory(clickedCategory: string)

    /* Drilldown: En serie har klickats */
    public abstract clickedSerie(clickedPoint: ClickDataPoint)

    protected abstract loadDrillDownParams(chartConfig, grouping: string, chartDataTypes: string[], clickedCategory: string)

    /* Beräkna config-objektets urval baserat på drilldown-kategori */
    protected abstract setConfigFromDataType(dataType: string, grouping: string, clickedDataItem: string,  chartConfig)

    /* Användaren har klickat "Gå tillbaka" på en drilldown-rapport */
    public drilledUp(): void {
        this.routeDataService.setSingleReadData(new DrillDownConfig(false, true)); // Visar i GUI att detta är en drilldown

        if (this.drillDownConfig)
            this.router.navigate(["/report/" + this.drillDownConfig.parentId]);
        else
            this.location.back();  
    }

    /* Hämta default för config */
    protected abstract getDefaultConfig(): void

    /* Sätt default för config */
    protected abstract setDefaultConfig(): void

    /* Sätt default startdatum */
    protected getDefaultStartDate(minYear, maxYear, chooseAllYears = false): string {
        let now = moment();
        let currentYear = now.format("YYYY");
        let periodStart = moment();

        if (minYear && chooseAllYears) {
            periodStart = moment(`${minYear}0101`);
        }
        else if (minYear && maxYear) {
            if (minYear == maxYear) periodStart = moment(`${minYear}0101`);
            else {
                const monthDay = (maxYear.toString() == currentYear) ? now.format("MMDD") : "1231";
                periodStart = moment(`${maxYear - 1}${monthDay}`);
            }
        } else if (maxYear) {
            periodStart = moment(`${maxYear - 1}1231`);
        } else if (minYear) {
            periodStart = moment(minYear.toString() == currentYear ? `${minYear}0101` : now.subtract(1, "years"));
        } else {
            periodStart = now.subtract(1, "years");
        }

        return periodStart.format("YYYY-MM-DD");
    }
    /* Sätt default slutdatum */
    protected getDefaultEndDate(maxYear): string {
        let now = moment();
        let periodSlut = moment();

        if (maxYear) {
            const monthDay = (maxYear.toString() == now.format("YYYY")) ? now.format("MMDD") : "1231";
            periodSlut = moment(`${maxYear}${monthDay}`);
        } 
        else periodSlut = now;

        return periodSlut.format("YYYY-MM-DD");
    }

    /* Har config default-värden? */
    protected checkIfDefaultConfig(): void {
        let defaultConfig = this.getDefaultConfig();
        let thisConfig = this.config.value;
        this.removeEmptyProperties(defaultConfig);
        this.removeEmptyProperties(thisConfig);
        let defaultConfigString = JSON.stringify(defaultConfig);
        let thisConfigString = JSON.stringify(thisConfig);
        this.configHasDefaultValues.next(defaultConfigString === thisConfigString);
    }
    private removeEmptyProperties(obj): void {
        let keys = Object.keys(obj);
        keys.forEach((key) => {
            let value = obj[key];
            if (value && value.toString() == "")
                obj[key] = undefined;
        });
    }

    /* Rensa alla urval */
    public clearParams() {
        localStorage.clear();
        this.setDefaultConfig();
        this.setFiltersFromConfig();
        this.removeFilterFromQuery();
    }

    /* Trigga nedladdning av ny rapportdata */
    public resaveConfig() {
        this.removeFilterFromQuery();
        this.saveConfigToLocalStorage();
    }

    /* Rensa url från url */
    protected removeFilterFromQuery(): void {
        this.resetSaveUserReport();
        this.locationGo("report/" + this.reportId);
    }
    /* Rensa sparad-rapport-id */
    protected resetSaveUserReport(): void {
        this.currentSharedReportId = null;
        this.canSaveUserReport.next(false);
    }

    /* Instantierar Utdataportalen-filter från config-rådata */
    protected abstract setFiltersFromConfig(): void

    /* Ett urval har uppdaterats. Sätt nytt config och trigga uppdatering av rapport */
    public abstract updateParam(paramData: ParamData): void

    /* Perioddata har uppdaterats */
    public abstract periodChange(periodData: PeriodData): void

    /* Uppdatera config och trigga nedladdning av ny rapportdata */
    protected abstract updateConfigAndTrigger(newConfig)

    /**
     * Höjd av diagram ändrad
     * (anropas från <xml-report>)
     */
    public currentChartHeight: string = "M";
    public abstract chartHeightChange(height: any)

    /* Visa felmeddelande */
    public loadReportError(error: any): void {
        this.messaging.showErrorMsg(error);
    }

    /**
     * Sparar ner config till localstorage
     * - Uppdaterar även gemensamma filter på localstorage
     */
    protected saveConfigToLocalStorage() {
        localStorage.setItem(this.reportId, JSON.stringify(this.config.getValue()));
        this.updateCommonFilters();
    }

    /**
     * Laddar filterinställningar från ett id och ställer in det på rapporten.
     */
    protected loadFilterSettings(id: string): void {
        this.sharedReportsService.getFilterSettings(id).subscribe((configObject) => {
            // Om rapporten är sparad som "Ett år tillbaka", och inte efter datumen
            if (configObject.currentYear) {
                let now = moment();
                configObject.stopdat = now.format("YYYY-MM-DD");
                configObject.startdat = now.subtract(1, "years").format("YYYY-MM-DD");
            }

            // Om rapporten har ett sparat språk som inte är det nuvarande -> byt språk
            if (configObject.language && configObject.language != this.language.getValue())
                this.langService.changeLanguage(configObject.language);

            if (configObject.apiBaseUrl == undefined) configObject.apiBaseUrl = "";

            this.reportId = configObject.reportName;

            this.config.next(configObject);
            this.setFiltersFromConfig();
            this.currentSharedReportId = id;
            this.canSaveUserReport.next(true);
        });
    }

    /**
     * Sparar ner filterinställningarna från aktuell rapport för att kunna delas via länk.
     * Det nya länkid't fås via Observablen som returneras.
     */
    public shareReport(): Observable<any> {
        let saveFilterSub = this.sharedReportsService.saveFilterSettings(JSON.stringify(this.config.getValue()));
        saveFilterSub.subscribe((sharedReport) => {
            if (sharedReport) {
                //console.log(sharedReport);
                this.currentSharedReportId = sharedReport.id;
                this.canSaveUserReport.next(true);
                this.locationGo("report/" + this.reportId, "?filter=" + sharedReport.id);
                this.messaging.showSuccessMsg(this.translate.instant("urval.share-report-info"));
            }
        });
        return saveFilterSub;
    }

    /* Spara rapport under inloggning */
    public abstract saveUserReport(): void

    /* Gruppering ändrad */
    public abstract changeGrouping(grouping): void


    /** 
     * Visar/Döljer urval i sidomeny till vänster 
     */
    public sideNavIsVisible: boolean = false;
    public phoneBrowserInUse: boolean = false;
    public toggleSideNav() {
        this.isPhoneBrowserInUse();   
        this.setAccordionHeight();
        this.toggleChartSize();
    }

    /* Används sidan av en mobil-webbläsare? */
    private isPhoneBrowserInUse() {
        this.phoneBrowserInUse = window.innerWidth < 768;
    }

    /* Sätt höjd på urvalsmenyn */
    private setAccordionHeight() {
        let isVisible = this.sideNavIsVisible;
        var urvalAccordion = document.getElementById("urval-accordion");
        if (urvalAccordion) {
            urvalAccordion.style.overflow = isVisible ? "hidden" : "";
            if (isVisible) {
                urvalAccordion.style.minHeight = "200px";
                urvalAccordion.style.maxHeight = "500px";
            } else {
                // Är 'period-tab' öppen el ej => olika min-height på sidenav
                let periodTab = document.getElementById("period-tab-header");
                if (periodTab && periodTab.classList.contains("active")) {
                    urvalAccordion.style.minHeight = "780px";
                }
                urvalAccordion.style.maxHeight = "10000px";
            }
        }
        this.sideNavIsVisible = !this.sideNavIsVisible;
    }

    /* Ändra bredd på chart efter att urvalmenyn visats/gömts */
    public toggleChartSize(): void {
        // (Göm highcharts & visa igen efter 250ms så ritas highcharts om)
        this.showHighcharts.next(false);
        setTimeout(() => this.showHighcharts.next(true), 250);
    }
}
